import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connectedTraining, schedule } from '../../lib/api';
import { markScheduleDataUpdated } from '../../lib/dataFreshness';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Dumbbell,
  MapPin,
  Medal,
  Plus,
  RefreshCcw,
  Sparkles,
  Timer,
  Trash2,
  Trophy,
  Waves,
  X,
  Zap,
} from 'lucide-react';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ru, enGB, fr } from 'date-fns/locale';

const LOCALES = { ru, en: enGB, fr };
const VIEW_MODES = ['month', 'week', 'agenda'];
const EVENT_TYPES = ['tennis', 'training', 'match', 'tournament', 'fitness', 'recovery', 'coach', 'custom'];
const EQUIPMENT_TYPES = ['treadmill', 'stationary_bike', 'elliptical', 'rowing_machine', 'smart_trainer', 'other'];

const emptyForm = () => ({
  title: '',
  type: 'tennis',
  date: format(new Date(), 'yyyy-MM-dd'),
  start_time: '10:00',
  end_time: '12:00',
  location: '',
  comment: '',
  recurring: false,
});

const emptyEquipmentTelemetry = () => ({
  elapsed_sec: '',
  distance_m: '',
  speed: '',
  incline: '',
  resistance: '',
  cadence: '',
  power: '',
  heart_rate: '',
  active_calories: '',
  perceived_exertion: '',
  fatigue_level: '',
  soreness_level: '',
  notes: '',
});

const eventDate = (event) => {
  const parsed = new Date(event.date);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const dateKey = (date) => format(date, 'yyyy-MM-dd');

const isUpcoming = (event) =>
  isAfter(eventDate(event), new Date()) || isSameDay(eventDate(event), new Date());

const typeMeta = (type) => {
  const normalized = String(type || 'custom').toLowerCase();
  if (['tennis', 'training', 'strength'].includes(normalized)) {
    return {
      labelKey: `schedule.eventType.${normalized}`,
      icon: <Dumbbell size={14} strokeWidth={1.8} />,
      badge: 'bg-lime-50 text-lime-700 ring-lime-100',
      dot: 'bg-lime-400',
      bar: 'border-lime-300',
    };
  }
  if (normalized === 'match') {
    return {
      labelKey: 'schedule.eventType.match',
      icon: <Trophy size={14} strokeWidth={1.8} />,
      badge: 'bg-rose-50 text-rose-700 ring-rose-100',
      dot: 'bg-rose-400',
      bar: 'border-rose-300',
    };
  }
  if (normalized === 'tournament') {
    return {
      labelKey: 'schedule.eventType.tournament',
      icon: <Medal size={14} strokeWidth={1.8} />,
      badge: 'bg-purple-50 text-purple-700 ring-purple-100',
      dot: 'bg-purple-400',
      bar: 'border-purple-300',
    };
  }
  if (['fitness', 'cardio'].includes(normalized)) {
    return {
      labelKey: `schedule.eventType.${normalized}`,
      icon: <Zap size={14} strokeWidth={1.8} />,
      badge: 'bg-blue-50 text-blue-700 ring-blue-100',
      dot: 'bg-blue-400',
      bar: 'border-blue-300',
    };
  }
  if (normalized === 'recovery') {
    return {
      labelKey: 'schedule.eventType.recovery',
      icon: <Waves size={14} strokeWidth={1.8} />,
      badge: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
      dot: 'bg-emerald-400',
      bar: 'border-emerald-300',
    };
  }
  if (normalized === 'coach') {
    return {
      labelKey: 'schedule.eventType.coach',
      icon: <Sparkles size={14} strokeWidth={1.8} />,
      badge: 'bg-amber-50 text-amber-700 ring-amber-100',
      dot: 'bg-amber-400',
      bar: 'border-amber-300',
    };
  }
  return {
    labelKey: normalized === 'general' ? 'schedule.eventType.general' : 'schedule.eventType.custom',
    icon: <CalendarDays size={14} strokeWidth={1.8} />,
    badge: 'bg-zinc-100 text-zinc-700 ring-zinc-200',
    dot: 'bg-zinc-400',
    bar: 'border-zinc-300',
  };
};

const EventTypeBadge = ({ type, t }) => {
  const meta = typeMeta(type);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black ring-1 ${meta.badge}`}>
      {meta.icon}
      {t(meta.labelKey)}
    </span>
  );
};

const ScheduleSkeleton = () => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="schedule-loading">
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="h-52 animate-pulse rounded-lg bg-zinc-200" />
      <div className="grid gap-4 md:grid-cols-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-32 animate-pulse rounded-lg bg-zinc-200" />
        ))}
      </div>
      <div className="h-[32rem] animate-pulse rounded-lg bg-zinc-200" />
    </div>
  </div>
);

const ScheduleErrorState = ({ onRetry, t }) => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="schedule-page">
    <div className="mx-auto max-w-7xl">
      <section className="rounded-lg border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-100">
          <RefreshCcw size={24} strokeWidth={1.8} />
        </div>
        <h1 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
          {t('schedule.loadError')}
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
          {t('schedule.errorBody')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
        >
          <RefreshCcw size={16} strokeWidth={1.8} />
          {t('schedule.retry')}
        </button>
      </section>
    </div>
  </div>
);

const EmptyScheduleState = ({ onAdd, t }) => (
  <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
      <CalendarDays size={26} strokeWidth={1.8} />
    </div>
    <h2 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
      {t('schedule.noEvents')}
    </h2>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
      {t('schedule.emptyBody')}
    </p>
    <button
      type="button"
      onClick={onAdd}
      className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
    >
      <Plus size={16} strokeWidth={1.8} />
      {t('schedule.add')}
    </button>
  </div>
);

const ViewToggle = ({ viewMode, setViewMode, t }) => (
  <div className="inline-grid min-h-11 grid-cols-3 rounded-lg bg-zinc-100 p-1">
    {VIEW_MODES.map((mode) => (
      <button
        key={mode}
        type="button"
        onClick={() => setViewMode(mode)}
        className={`rounded-md px-3 text-sm font-black transition ${
          viewMode === mode ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-500 hover:text-zinc-900'
        }`}
      >
        {t(`schedule.views.${mode}`)}
      </button>
    ))}
  </div>
);

const ScheduleHero = ({ todayEvents, nextEvent, upcomingCount, onAdd, t, locale }) => (
  <section className="relative overflow-hidden rounded-lg bg-zinc-950 p-5 text-white shadow-sm md:p-7">
    <div className="absolute inset-x-0 top-0 h-px bg-lime-300" />
    <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-lime-300 opacity-10 blur-3xl" />
    <div className="relative grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-lime-200">
          <CalendarDays size={14} strokeWidth={1.8} />
          {t('schedule.calendar')}
        </div>
        <h1 className="mt-4 text-3xl font-black tracking-normal text-white md:text-4xl">
          {t('schedule.title')}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
          {t('schedule.subtitle')}
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
          data-testid="add-training-button"
        >
          <Plus size={16} strokeWidth={1.8} />
          {t('schedule.add')}
        </button>
      </div>
      <div className="rounded-lg border border-white/10 bg-white/10 p-4">
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
          {t('schedule.today')}
        </p>
        <p className="mt-2 text-4xl font-black tracking-normal text-white">{todayEvents.length}</p>
        <p className="mt-1 text-sm text-zinc-300">
          {t('schedule.todayCount')}
        </p>
        <div className="mt-5 rounded-lg bg-zinc-950/40 p-3">
          <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-400">
            {t('schedule.nextEvent')}
          </p>
          {nextEvent ? (
            <div className="mt-2">
              <p className="font-black text-white">{nextEvent.title}</p>
              <p className="mt-1 text-sm text-zinc-300">
                {format(eventDate(nextEvent), 'dd MMM', { locale })} · {nextEvent.start_time} - {nextEvent.end_time}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-sm text-zinc-300">{t('schedule.noEvents')}</p>
          )}
        </div>
        <p className="mt-3 text-sm text-zinc-300">
          {t('schedule.upcomingCount', { count: upcomingCount })}
        </p>
      </div>
    </div>
  </section>
);

const SummaryCard = ({ icon, label, value, detail, tone = 'lime' }) => {
  const toneClass =
    tone === 'rose'
      ? 'bg-rose-50 text-rose-700 ring-rose-100'
      : tone === 'blue'
      ? 'bg-blue-50 text-blue-700 ring-blue-100'
      : 'bg-lime-50 text-lime-700 ring-lime-100';
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-zinc-500">{label}</p>
          <p className="mt-2 text-3xl font-black tracking-normal text-zinc-950">{value}</p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ring-1 ${toneClass}`}>
          {icon}
        </div>
      </div>
      {detail ? <p className="mt-3 text-sm leading-5 text-zinc-500">{detail}</p> : null}
    </div>
  );
};

const EventCard = ({ event, onDelete, t, locale, compact = false }) => {
  const meta = typeMeta(event.type);
  return (
    <article className={`rounded-lg border border-zinc-200 border-l-4 ${meta.bar} bg-white p-4 shadow-sm`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <EventTypeBadge type={event.type} t={t} />
          <h3 className="mt-3 font-black text-zinc-950">{event.title}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-zinc-500">
            <span className="inline-flex items-center gap-1">
              <Clock3 size={14} strokeWidth={1.8} />
              {format(eventDate(event), compact ? 'dd MMM' : 'dd MMMM yyyy', { locale })}
            </span>
            <span>{event.start_time} - {event.end_time}</span>
          </p>
          {event.location ? (
            <p className="mt-2 flex items-center gap-1 text-sm text-zinc-500">
              <MapPin size={14} strokeWidth={1.8} />
              {event.location}
            </p>
          ) : null}
          {event.comment ? (
            <p className="mt-3 line-clamp-2 text-sm leading-5 text-zinc-500">{event.comment}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => onDelete(event.id)}
          className="inline-flex min-h-11 w-11 shrink-0 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-rose-50 hover:text-rose-700"
          aria-label={t('schedule.deleteEvent')}
          data-testid={`delete-event-${event.id}`}
        >
          <Trash2 size={18} strokeWidth={1.8} />
        </button>
      </div>
    </article>
  );
};

const prescriptionText = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');

const prescriptionLabel = (value) => prescriptionText(value).replace(/_/g, ' ');

const prescriptionDate = (prescription, eventsById) => {
  if (prescription?.scheduled_for) {
    const parsed = new Date(prescription.scheduled_for);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  const linkedEvent = eventsById.get(prescription?.linked_schedule_event_id);
  if (linkedEvent) return eventDate(linkedEvent);
  return null;
};

const prescriptionDuration = (seconds, t) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '';
  return `${Math.round(value / 60)} ${t('common.min', 'min')}`;
};

const sortedPrescriptionSegments = (prescription) =>
  (Array.isArray(prescription?.segments) ? prescription.segments : [])
    .filter(Boolean)
    .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));

const activeExecutionStatuses = new Set(['planned', 'in_progress', 'partial']);

const numericFeedbackValue = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number >= 1 && number <= 10 ? number : undefined;
};

const compactPayload = (payload) =>
  Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== ''));

const numericTelemetryValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : undefined;
};

const segmentResultFor = (segment, index, status) => ({
  segment_order: Number(segment?.order || index + 1),
  segment_name: prescriptionText(segment?.name) || `Segment ${index + 1}`,
  status,
});

const upsertSegmentResult = (results, nextResult) => {
  const existing = Array.isArray(results) ? results : [];
  const withoutCurrent = existing.filter((item) => Number(item?.segment_order) !== nextResult.segment_order);
  return [...withoutCurrent, nextResult].sort((a, b) => Number(a.segment_order || 0) - Number(b.segment_order || 0));
};

const workoutExecutionDate = (execution) => {
  const parsed = new Date(execution?.started_at || execution?.updated_at || execution?.completed_at || execution?.created_at || 0);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

const latestExecutionForPrescription = (executions, prescriptionId) => {
  const matching = (Array.isArray(executions) ? executions : []).filter((execution) => execution?.prescription_id === prescriptionId);
  const active = matching.filter((execution) => activeExecutionStatuses.has(String(execution?.status || '')));
  const source = active.length ? active : matching;
  return [...source].sort((a, b) => workoutExecutionDate(b) - workoutExecutionDate(a))[0] || null;
};

const PrescriptionDetail = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <p className="rounded-lg bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-700">
      <span className="font-bold text-zinc-500">{label}:</span> {value}
    </p>
  );
};

const SegmentTargets = ({ segment, t }) => (
  <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold text-zinc-500">
    {segment?.duration_sec ? <span>{prescriptionDuration(segment.duration_sec, t)}</span> : null}
    {segment?.distance_m ? <span>{Math.round(Number(segment.distance_m))} m</span> : null}
    {segment?.target_speed ? <span>{t('schedule.targetSpeed')}: {segment.target_speed}</span> : null}
    {segment?.target_incline ? <span>{t('schedule.targetIncline')}: {segment.target_incline}</span> : null}
    {segment?.target_resistance ? <span>{t('schedule.targetResistance')}: {segment.target_resistance}</span> : null}
    {segment?.target_cadence ? <span>{t('schedule.targetCadence')}: {segment.target_cadence}</span> : null}
    {segment?.target_power ? <span>{t('schedule.targetPower')}: {segment.target_power}</span> : null}
    {segment?.target_heart_rate_zone ? <span>{t('schedule.targetHeartRateZone')}: {segment.target_heart_rate_zone}</span> : null}
  </div>
);

const PrescriptionScheduleCard = ({ prescription, scheduledDate, linkedEvent, onOpenGuidance, t, locale }) => {
  const segments = sortedPrescriptionSegments(prescription);
  const keySegments = segments.slice(0, 3);
  const warningCues = segments.map((segment) => prescriptionText(segment?.warning_cue)).filter(Boolean);
  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {scheduledDate ? format(scheduledDate, 'dd MMMM yyyy', { locale }) : t('schedule.prescriptionUnscheduled')}
          </p>
          <h3 className="mt-1 font-black text-zinc-950">{prescriptionText(prescription?.title) || t('schedule.coachPrescription')}</h3>
          {prescription?.purpose ? (
            <p className="mt-2 text-sm font-semibold leading-6 text-zinc-600">{prescription.purpose}</p>
          ) : null}
        </div>
        {prescription?.status ? (
          <span className="rounded-full bg-lime-50 px-3 py-1 text-xs font-black text-lime-700 ring-1 ring-lime-100">
            {prescriptionLabel(prescription.status)}
          </span>
        ) : null}
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <PrescriptionDetail label={t('schedule.workoutType')} value={prescriptionLabel(prescription?.workout_type)} />
        <PrescriptionDetail label={t('schedule.targetDevice')} value={prescriptionLabel(prescription?.target_device_type)} />
        <PrescriptionDetail label={t('schedule.linkedScheduleEvent')} value={linkedEvent?.title} />
      </div>

      {prescription?.safety_notes ? (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-bold leading-6 text-amber-800 ring-1 ring-amber-100">
          {t('schedule.safetyNotes')}: {prescription.safety_notes}
        </p>
      ) : null}

      {keySegments.length ? (
        <div className="mt-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
            {t('schedule.keySegments')}
          </p>
          <div className="mt-2 space-y-2">
            {keySegments.map((segment, index) => (
              <div key={`${segment?.order || index}-${segment?.name}`} className="rounded-lg bg-zinc-50 p-3 text-sm">
                <p className="font-black text-zinc-950">
                  {segment?.order ? `${segment.order}. ` : ''}{prescriptionText(segment?.name) || t('schedule.segment')}
                </p>
                <SegmentTargets segment={segment} t={t} />
                {segment?.instruction ? (
                  <p className="mt-2 font-semibold leading-6 text-zinc-600">{segment.instruction}</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {warningCues.length ? (
        <div className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-sm font-bold leading-6 text-amber-800 ring-1 ring-amber-100">
          <p>{t('schedule.warningCues')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {warningCues.slice(0, 3).map((cue) => (
              <li key={cue}>{cue}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="mt-4 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-600">
        {t('schedule.equipmentControlNotEnabled')}
      </p>
      <button
        type="button"
        onClick={() => onOpenGuidance(prescription)}
        className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
      >
        {t('schedule.openWorkoutProgram')}
      </button>
    </article>
  );
};

const WorkoutGuidanceModal = ({ prescription, linkedEvent, initialExecution, onExecutionChanged, onClose, t, locale }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [execution, setExecution] = useState(initialExecution || null);
  const [segmentResults, setSegmentResults] = useState(initialExecution?.segment_results || []);
  const [executionSaving, setExecutionSaving] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    rpe: initialExecution?.rpe || initialExecution?.perceived_exertion || '',
    athlete_notes: initialExecution?.athlete_notes || '',
    fatigue_level: initialExecution?.fatigue_level || '',
    soreness_level: initialExecution?.soreness_level || '',
  });
  const segments = useMemo(() => sortedPrescriptionSegments(prescription), [prescription]);
  const currentSegment = segments[currentIndex];
  const scheduledDate = prescriptionDate(prescription, new Map(linkedEvent ? [[linkedEvent.id, linkedEvent]] : []));

  useEffect(() => {
    setCurrentIndex(0);
    setExecution(initialExecution || null);
    setSegmentResults(initialExecution?.segment_results || []);
    setFeedbackForm({
      rpe: initialExecution?.rpe || initialExecution?.perceived_exertion || '',
      athlete_notes: initialExecution?.athlete_notes || '',
      fatigue_level: initialExecution?.fatigue_level || '',
      soreness_level: initialExecution?.soreness_level || '',
    });
  }, [prescription?.id, initialExecution]);

  const syncExecution = (nextExecution) => {
    setExecution(nextExecution);
    setSegmentResults(Array.isArray(nextExecution?.segment_results) ? nextExecution.segment_results : []);
    onExecutionChanged?.(nextExecution);
  };

  const buildFeedbackPayload = (nextSegmentResults = segmentResults) =>
    compactPayload({
      rpe: numericFeedbackValue(feedbackForm.rpe),
      perceived_exertion: numericFeedbackValue(feedbackForm.rpe),
      athlete_notes: prescriptionText(feedbackForm.athlete_notes) || undefined,
      fatigue_level: numericFeedbackValue(feedbackForm.fatigue_level),
      soreness_level: numericFeedbackValue(feedbackForm.soreness_level),
      segment_results: Array.isArray(nextSegmentResults) ? nextSegmentResults : [],
    });

  const startExecution = async () => {
    if (!prescription?.id) return;
    setExecutionSaving(true);
    try {
      const response = await connectedTraining.createWorkoutExecution({
        prescription_id: prescription.id,
        linked_schedule_event_id: prescription.linked_schedule_event_id || linkedEvent?.id,
      });
      syncExecution(response.data);
      toast.success(t('schedule.executionInProgress'));
    } catch (error) {
      toast.error(t('schedule.executionSaveError'));
    } finally {
      setExecutionSaving(false);
    }
  };

  const saveExecution = async (payload) => {
    if (!execution?.id) return null;
    setExecutionSaving(true);
    try {
      const response = await connectedTraining.updateWorkoutExecution(execution.id, payload);
      syncExecution(response.data);
      toast.success(t('schedule.executionSaveSuccess'));
      return response.data;
    } catch (error) {
      toast.error(t('schedule.executionSaveError'));
      return null;
    } finally {
      setExecutionSaving(false);
    }
  };

  const markSegment = async (status) => {
    if (!currentSegment || !execution?.id) return;
    const nextResults = upsertSegmentResult(segmentResults, segmentResultFor(currentSegment, currentIndex, status));
    await saveExecution({ status: 'partial', segment_results: nextResults });
  };

  const saveFeedback = async () => {
    await saveExecution(buildFeedbackPayload());
  };

  const completeExecution = async () => {
    if (!execution?.id) return;
    setExecutionSaving(true);
    try {
      const response = await connectedTraining.completeWorkoutExecution(execution.id, buildFeedbackPayload());
      syncExecution(response.data);
      toast.success(t('schedule.executionCompleted'));
    } catch (error) {
      toast.error(t('schedule.executionSaveError'));
    } finally {
      setExecutionSaving(false);
    }
  };

  if (!prescription) return null;

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < segments.length - 1;
  const currentSegmentResult = segmentResults.find(
    (item) => Number(item?.segment_order) === Number(currentSegment?.order || currentIndex + 1),
  );
  const executionStatus = execution?.status ? prescriptionLabel(execution.status) : '';
  const hasActiveExecution = execution?.id && activeExecutionStatuses.has(String(execution.status || ''));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-zinc-950/70 p-4" role="dialog" aria-modal="true">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
              {t('schedule.workoutGuidance')}
            </p>
            <h2 className="mt-1 text-2xl font-black tracking-normal text-zinc-950">
              {prescriptionText(prescription?.title) || t('schedule.coachPrescription')}
            </h2>
            {prescription?.purpose ? (
              <p className="mt-2 text-sm font-semibold leading-6 text-zinc-600">{prescription.purpose}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 w-11 shrink-0 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900"
            aria-label={t('schedule.close')}
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <PrescriptionDetail label={t('schedule.workoutType')} value={prescriptionLabel(prescription?.workout_type)} />
          <PrescriptionDetail label={t('schedule.targetDevice')} value={prescriptionLabel(prescription?.target_device_type)} />
          <PrescriptionDetail label={t('schedule.status')} value={prescriptionLabel(prescription?.status)} />
          <PrescriptionDetail label={t('schedule.executionStatus')} value={executionStatus} />
          <PrescriptionDetail label={t('schedule.scheduledFor')} value={scheduledDate ? format(scheduledDate, 'dd MMMM yyyy', { locale }) : ''} />
          <PrescriptionDetail label={t('schedule.linkedScheduleEvent')} value={linkedEvent?.title} />
        </div>

        <p className="mt-4 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-700">
          {t('schedule.manualExecutionTrackingOnly')}
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          {!execution?.id ? (
            <button
              type="button"
              onClick={startExecution}
              disabled={executionSaving}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('schedule.startWorkoutExecution')}
            </button>
          ) : (
            <span className="inline-flex min-h-11 items-center rounded-lg bg-lime-50 px-4 text-sm font-black text-lime-800 ring-1 ring-lime-100">
              {execution?.status === 'completed' ? t('schedule.executionCompleted') : t('schedule.executionInProgress')}
            </span>
          )}
          {execution?.id && execution?.status !== 'completed' ? (
            <button
              type="button"
              onClick={completeExecution}
              disabled={executionSaving}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('schedule.completeWorkoutExecution')}
            </button>
          ) : null}
        </div>

        {prescription?.safety_notes ? (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-bold leading-6 text-amber-800 ring-1 ring-amber-100">
            {t('schedule.safetyNotes')}: {prescription.safety_notes}
          </p>
        ) : null}

        {segments.length ? (
          <div className="mt-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                {t('schedule.segmentProgress', { current: currentIndex + 1, total: segments.length })}
              </p>
              <p className="text-sm font-black text-zinc-950">{t('schedule.currentSegment')}</p>
            </div>

            <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
                {currentSegment?.order ? `${t('schedule.segment')} ${currentSegment.order}` : t('schedule.segment')}
              </p>
              <h3 className="mt-1 text-xl font-black text-zinc-950">
                {prescriptionText(currentSegment?.name) || t('schedule.segment')}
              </h3>
              <SegmentTargets segment={currentSegment} t={t} />
              {currentSegment?.instruction ? (
                <p className="mt-3 rounded-lg bg-lime-50 px-3 py-2 text-sm font-bold leading-6 text-lime-900 ring-1 ring-lime-100">
                  {currentSegment.instruction}
                </p>
              ) : null}
              {currentSegment?.warning_cue ? (
                <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-bold leading-6 text-amber-800 ring-1 ring-amber-100">
                  {t('schedule.warningCue')}: {currentSegment.warning_cue}
                </p>
              ) : null}
              {currentSegment?.requires_user_confirmation_before_control ? (
                <p className="mt-3 text-sm font-bold leading-6 text-zinc-500">
                  {t('schedule.futureControlRequiresConfirmation')}
                </p>
              ) : null}
              {currentSegmentResult?.status ? (
                <p className="mt-3 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-600">
                  {t('schedule.segmentStatus')}: {prescriptionLabel(currentSegmentResult.status)}
                </p>
              ) : null}
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => markSegment('completed')}
                  disabled={!hasActiveExecution || executionSaving}
                  className="inline-flex min-h-11 items-center justify-center rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('schedule.markSegmentCompleted')}
                </button>
                <button
                  type="button"
                  onClick={() => markSegment('skipped')}
                  disabled={!hasActiveExecution || executionSaving}
                  className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('schedule.skipSegment')}
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setCurrentIndex((value) => Math.max(0, value - 1))}
                disabled={!canGoPrevious}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft size={16} strokeWidth={1.8} />
                {t('schedule.previousSegment')}
              </button>
              <button
                type="button"
                onClick={() => setCurrentIndex((value) => Math.min(segments.length - 1, value + 1))}
                disabled={!canGoNext}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('schedule.nextSegment')}
                <ChevronRight size={16} strokeWidth={1.8} />
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-5 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
            {t('schedule.noSegments')}
          </p>
        )}

        <div className="mt-5 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
            {t('schedule.executionFeedback')}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-bold text-zinc-700">
              {t('schedule.rpe')}
              <input
                type="number"
                min="1"
                max="10"
                value={feedbackForm.rpe}
                onChange={(event) => setFeedbackForm((prev) => ({ ...prev, rpe: event.target.value }))}
                className="mt-2 min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
            </label>
            <label className="block text-sm font-bold text-zinc-700">
              {t('schedule.fatigueLevel')}
              <input
                type="number"
                min="1"
                max="10"
                value={feedbackForm.fatigue_level}
                onChange={(event) => setFeedbackForm((prev) => ({ ...prev, fatigue_level: event.target.value }))}
                className="mt-2 min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
            </label>
            <label className="block text-sm font-bold text-zinc-700">
              {t('schedule.sorenessLevel')}
              <input
                type="number"
                min="1"
                max="10"
                value={feedbackForm.soreness_level}
                onChange={(event) => setFeedbackForm((prev) => ({ ...prev, soreness_level: event.target.value }))}
                className="mt-2 min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
            </label>
          </div>
          <label className="mt-3 block text-sm font-bold text-zinc-700">
            {t('schedule.athleteNotes')}
            <textarea
              rows={3}
              value={feedbackForm.athlete_notes}
              onChange={(event) => setFeedbackForm((prev) => ({ ...prev, athlete_notes: event.target.value }))}
              className="mt-2 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            />
          </label>
          <button
            type="button"
            onClick={saveFeedback}
            disabled={!execution?.id || executionSaving}
            className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('schedule.saveExecutionFeedback')}
          </button>
        </div>
      </div>
    </div>
  );
};

const EquipmentWorkoutTrackingPanel = ({ activeSessions, onRefresh, t }) => {
  const [equipmentType, setEquipmentType] = useState('treadmill');
  const [targetPrompt, setTargetPrompt] = useState('');
  const [telemetryForm, setTelemetryForm] = useState(emptyEquipmentTelemetry);
  const [saving, setSaving] = useState(false);
  const activeSession = activeSessions[0] || null;
  const telemetry = activeSession?.current_telemetry || null;

  const telemetryPayload = () => ({
    telemetry: compactPayload({
      elapsed_sec: numericTelemetryValue(telemetryForm.elapsed_sec),
      distance_m: numericTelemetryValue(telemetryForm.distance_m),
      speed: numericTelemetryValue(telemetryForm.speed),
      incline: telemetryForm.incline === '' ? undefined : Number(telemetryForm.incline),
      resistance: numericTelemetryValue(telemetryForm.resistance),
      cadence: numericTelemetryValue(telemetryForm.cadence),
      power: numericTelemetryValue(telemetryForm.power),
      heart_rate: numericTelemetryValue(telemetryForm.heart_rate),
      active_calories: numericTelemetryValue(telemetryForm.active_calories),
      perceived_exertion: numericFeedbackValue(telemetryForm.perceived_exertion),
      fatigue_level: numericFeedbackValue(telemetryForm.fatigue_level),
      soreness_level: numericFeedbackValue(telemetryForm.soreness_level),
      notes: prescriptionText(telemetryForm.notes) || undefined,
      source: 'manual',
    }),
  });

  const startSession = async () => {
    setSaving(true);
    try {
      await connectedTraining.startEquipmentWorkoutSession({
        equipment_type: equipmentType,
        tracking_mode: 'manual_tracking',
        telemetry_sources: ['manual'],
        title: t('schedule.equipmentWorkoutSessionTitle'),
        target_prompt: prescriptionText(targetPrompt) || undefined,
      });
      setTargetPrompt('');
      toast.success(t('schedule.equipmentTrackingStarted'));
      await onRefresh();
    } catch (error) {
      toast.error(t('schedule.equipmentTrackingError'));
    } finally {
      setSaving(false);
    }
  };

  const saveTelemetry = async () => {
    if (!activeSession?.id) return;
    setSaving(true);
    try {
      await connectedTraining.updateEquipmentWorkoutTelemetry(activeSession.id, telemetryPayload());
      toast.success(t('schedule.equipmentTelemetrySaved'));
      await onRefresh();
    } catch (error) {
      toast.error(t('schedule.equipmentTelemetryError'));
    } finally {
      setSaving(false);
    }
  };

  const completeSession = async () => {
    if (!activeSession?.id) return;
    setSaving(true);
    try {
      await connectedTraining.completeEquipmentWorkoutSession(activeSession.id, telemetryPayload());
      setTelemetryForm(emptyEquipmentTelemetry());
      toast.success(t('schedule.equipmentTrackingCompleted'));
      await onRefresh();
    } catch (error) {
      toast.error(t('schedule.equipmentTelemetryError'));
    } finally {
      setSaving(false);
    }
  };

  const cancelSession = async () => {
    if (!activeSession?.id) return;
    setSaving(true);
    try {
      await connectedTraining.cancelEquipmentWorkoutSession(activeSession.id);
      toast.success(t('schedule.equipmentTrackingCancelled'));
      await onRefresh();
    } catch (error) {
      toast.error(t('schedule.equipmentTelemetryError'));
    } finally {
      setSaving(false);
    }
  };

  const updateTelemetryField = (field, value) => {
    setTelemetryForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {t('schedule.equipmentWorkoutKicker')}
          </p>
          <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">
            {t('schedule.equipmentWorkoutTitle')}
          </h2>
        </div>
        {activeSession ? (
          <span className="rounded-full bg-lime-50 px-3 py-1 text-xs font-black text-lime-700 ring-1 ring-lime-100">
            {t('schedule.equipmentSessionActive')}
          </span>
        ) : null}
      </div>

      <p className="rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-700">
        {t('schedule.equipmentTrackingSafetyNote')}
      </p>

      {!activeSession ? (
        <div className="mt-4 grid gap-3 md:grid-cols-[220px_1fr_auto] md:items-end">
          <label className="block text-sm font-bold text-zinc-700">
            {t('schedule.equipmentType')}
            <select
              value={equipmentType}
              onChange={(event) => setEquipmentType(event.target.value)}
              className="mt-2 min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            >
              {EQUIPMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`schedule.equipmentTypes.${type}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-bold text-zinc-700">
            {t('schedule.targetPrompt')}
            <input
              type="text"
              value={targetPrompt}
              onChange={(event) => setTargetPrompt(event.target.value)}
              className="mt-2 min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            />
          </label>
          <button
            type="button"
            onClick={startSession}
            disabled={saving}
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('schedule.startEquipmentTracking')}
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <PrescriptionDetail label={t('schedule.equipmentType')} value={t(`schedule.equipmentTypes.${activeSession.equipment_type}`)} />
            <PrescriptionDetail label={t('schedule.status')} value={prescriptionLabel(activeSession.session_mode)} />
            <PrescriptionDetail label={t('schedule.trackingMode')} value={prescriptionLabel(activeSession.tracking_mode)} />
          </div>
          {telemetry ? (
            <div className="rounded-lg bg-lime-50 px-3 py-2 text-sm font-bold leading-6 text-lime-900 ring-1 ring-lime-100">
              {t('schedule.currentTelemetry')}: {telemetry.elapsed_sec ? `${telemetry.elapsed_sec}s` : ''}
              {telemetry.distance_m ? ` · ${telemetry.distance_m}m` : ''}
              {telemetry.heart_rate ? ` · ${telemetry.heart_rate} bpm` : ''}
              {telemetry.active_calories ? ` · ${telemetry.active_calories} kcal` : ''}
            </div>
          ) : (
            <p className="rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
              {t('schedule.noRealTelemetryYet')}
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[
              ['elapsed_sec', 'elapsedSec'],
              ['distance_m', 'distanceM'],
              ['heart_rate', 'heartRate'],
              ['active_calories', 'activeCalories'],
              ['perceived_exertion', 'rpe'],
              ['speed', 'speed'],
              ['incline', 'incline'],
              ['resistance', 'resistance'],
              ['cadence', 'cadence'],
              ['power', 'power'],
              ['fatigue_level', 'fatigueLevel'],
              ['soreness_level', 'sorenessLevel'],
            ].map(([field, labelKey]) => (
              <label key={field} className="block text-sm font-bold text-zinc-700">
                {t(`schedule.${labelKey}`)}
                <input
                  type="number"
                  value={telemetryForm[field]}
                  onChange={(event) => updateTelemetryField(field, event.target.value)}
                  className="mt-2 min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                />
              </label>
            ))}
          </div>
          <label className="block text-sm font-bold text-zinc-700">
            {t('schedule.notes')}
            <textarea
              rows={2}
              value={telemetryForm.notes}
              onChange={(event) => updateTelemetryField('notes', event.target.value)}
              className="mt-2 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={saveTelemetry}
              disabled={saving}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('schedule.saveManualTelemetry')}
            </button>
            <button
              type="button"
              onClick={completeSession}
              disabled={saving}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('schedule.completeEquipmentTracking')}
            </button>
            <button
              type="button"
              onClick={cancelSession}
              disabled={saving}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-rose-200 bg-white px-4 text-sm font-black text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('schedule.cancelEquipmentTracking')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const PrescriptionSchedulePanel = ({ prescriptions, events, onOpenGuidance, t, locale }) => {
  const eventsById = useMemo(() => new Map(events.map((event) => [event.id, event])), [events]);
  const scheduledPrescriptions = useMemo(
    () =>
      prescriptions
        .map((prescription) => ({
          prescription,
          scheduledDate: prescriptionDate(prescription, eventsById),
          linkedEvent: eventsById.get(prescription?.linked_schedule_event_id),
        }))
        .filter((item) => item.scheduledDate || item.linkedEvent)
        .sort((a, b) => (a.scheduledDate || new Date(0)) - (b.scheduledDate || new Date(0))),
    [eventsById, prescriptions],
  );

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {t('schedule.coachProgramsKicker')}
          </p>
          <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">
            {t('schedule.coachProgramsTitle')}
          </h2>
        </div>
        <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-black text-zinc-600 ring-1 ring-zinc-200">
          {scheduledPrescriptions.length}
        </span>
      </div>
      {scheduledPrescriptions.length ? (
        <div className="grid gap-3">
          {scheduledPrescriptions.slice(0, 6).map(({ prescription, scheduledDate, linkedEvent }) => (
            <PrescriptionScheduleCard
              key={prescription?.id || `${prescription?.title}-${scheduledDate}`}
              prescription={prescription}
              scheduledDate={scheduledDate}
              linkedEvent={linkedEvent}
              onOpenGuidance={onOpenGuidance}
              t={t}
              locale={locale}
            />
          ))}
        </div>
      ) : (
        <p className="rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
          {t('schedule.noScheduledPrescriptions')}
        </p>
      )}
    </section>
  );
};

const CalendarGrid = ({ days, currentDate, getEventsForDay, openCreateForDay, weekDays, t }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
    <div className="grid grid-cols-7 gap-2" data-testid="calendar-grid">
      {(Array.isArray(weekDays) ? weekDays : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']).map((day) => (
        <div key={day} className="py-2 text-center text-xs font-black uppercase tracking-[0.12em] text-zinc-400">
          {day}
        </div>
      ))}
      {days.map((day) => {
        const dayEvents = getEventsForDay(day);
        const inCurrentMonth = isSameMonth(day, currentDate);
        const today = isToday(day);
        return (
          <button
            type="button"
            key={day.toString()}
            onClick={() => openCreateForDay(day)}
            className={`min-h-28 rounded-lg border p-2 text-left transition ${
              today
                ? 'border-lime-300 bg-lime-50 ring-1 ring-lime-100'
                : 'border-zinc-200 bg-white hover:border-lime-200 hover:bg-lime-50/40'
            } ${!inCurrentMonth ? 'opacity-45' : ''}`}
            data-testid={`calendar-day-${dateKey(day)}`}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className={`text-sm font-black ${today ? 'text-lime-800' : 'text-zinc-950'}`}>
                {format(day, 'd')}
              </span>
              {dayEvents.length > 0 ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-zinc-950 px-1.5 text-[10px] font-black text-lime-300">
                  {dayEvents.length}
                </span>
              ) : null}
            </div>
            <div className="space-y-1">
              {dayEvents.slice(0, 3).map((event) => {
                const meta = typeMeta(event.type);
                return (
                  <div key={event.id} className="flex items-center gap-1 rounded-md bg-zinc-50 px-2 py-1">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${meta.dot}`} />
                    <span className="truncate text-xs font-bold text-zinc-700">{event.title}</span>
                  </div>
                );
              })}
              {dayEvents.length > 3 ? (
                <p className="px-2 text-xs font-bold text-zinc-400">
                  +{dayEvents.length - 3} {t('schedule.more')}
                </p>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const WeekStrip = ({ weekDays, selectedDate, setSelectedDate, getEventsForDay, locale }) => (
  <div className="grid grid-cols-7 gap-2">
    {weekDays.map((day) => {
      const count = getEventsForDay(day).length;
      const selected = isSameDay(day, selectedDate);
      return (
        <button
          type="button"
          key={day.toString()}
          onClick={() => setSelectedDate(day)}
          className={`min-h-24 rounded-lg border p-3 text-center transition ${
            selected ? 'border-lime-300 bg-lime-50 ring-1 ring-lime-100' : 'border-zinc-200 bg-white hover:bg-zinc-50'
          }`}
        >
          <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-400">{format(day, 'EEE', { locale })}</p>
          <p className={`mt-2 text-2xl font-black ${selected ? 'text-lime-800' : 'text-zinc-950'}`}>
            {format(day, 'd')}
          </p>
          <p className="mt-1 text-xs font-bold text-zinc-500">{count}</p>
        </button>
      );
    })}
  </div>
);

const DayAgendaList = ({ title, events, onDelete, onAdd, t, locale }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm" data-testid="upcoming-events">
    <div className="mb-4 flex items-center justify-between gap-4">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
          {t('schedule.agenda')}
        </p>
        <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{title}</h2>
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
      >
        <Plus size={16} strokeWidth={1.8} />
        {t('schedule.add')}
      </button>
    </div>
    {events.length === 0 ? (
      <EmptyScheduleState onAdd={onAdd} t={t} />
    ) : (
      <div className="grid gap-3">
        {events.map((event) => (
          <EventCard key={event.id} event={event} onDelete={onDelete} t={t} locale={locale} />
        ))}
      </div>
    )}
  </section>
);

const AddEventModal = ({ formData, setFormData, onSubmit, onClose, t }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-zinc-950/60 p-4"
    data-testid="add-training-modal"
    role="dialog"
    aria-modal="true"
  >
    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {t('schedule.addEvent')}
          </p>
          <h2 className="mt-1 text-2xl font-black tracking-normal text-zinc-950">
            {t('schedule.createEvent')}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex min-h-11 w-11 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900"
          aria-label={t('schedule.close')}
        >
          <X size={20} strokeWidth={1.8} />
        </button>
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-bold text-zinc-700">{t('schedule.eventTitle')}</label>
          <input
            type="text"
            className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            value={formData.title}
            onChange={(event) => setFormData({ ...formData, title: event.target.value })}
            required
            data-testid="input-title"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold text-zinc-700">{t('schedule.eventTypeLabel')}</label>
          <select
            className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            value={formData.type}
            onChange={(event) => setFormData({ ...formData, type: event.target.value })}
            data-testid="select-type"
          >
            {EVENT_TYPES.map((type) => {
              const meta = typeMeta(type);
              return (
                <option key={type} value={type}>
                  {t(meta.labelKey)}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold text-zinc-700">{t('schedule.date')}</label>
          <input
            type="date"
            className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            value={formData.date}
            onChange={(event) => setFormData({ ...formData, date: event.target.value })}
            required
            data-testid="input-date"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-2 block text-sm font-bold text-zinc-700">{t('schedule.startTime')}</label>
            <input
              type="time"
              className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              value={formData.start_time}
              onChange={(event) => setFormData({ ...formData, start_time: event.target.value })}
              required
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-bold text-zinc-700">{t('schedule.endTime')}</label>
            <input
              type="time"
              className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              value={formData.end_time}
              onChange={(event) => setFormData({ ...formData, end_time: event.target.value })}
              required
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold text-zinc-700">{t('schedule.location')}</label>
          <input
            type="text"
            className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            value={formData.location}
            onChange={(event) => setFormData({ ...formData, location: event.target.value })}
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold text-zinc-700">{t('schedule.notes')}</label>
          <textarea
            className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            rows={3}
            value={formData.comment}
            onChange={(event) => setFormData({ ...formData, comment: event.target.value })}
          />
        </div>

        <label className="flex min-h-11 items-center gap-3 rounded-lg bg-zinc-50 px-3 text-sm font-bold text-zinc-700">
          <input
            type="checkbox"
            checked={formData.recurring}
            onChange={(event) => setFormData({ ...formData, recurring: event.target.checked })}
            className="h-4 w-4 accent-lime-500"
          />
          {t('schedule.recurring')}
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
            data-testid="submit-training"
          >
            {t('schedule.add')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50"
            data-testid="cancel-training"
          >
            {t('schedule.cancel')}
          </button>
        </div>
      </form>
    </div>
  </div>
);

export const SchedulePage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [workoutPrescriptions, setWorkoutPrescriptions] = useState([]);
  const [workoutExecutions, setWorkoutExecutions] = useState([]);
  const [activeEquipmentSessions, setActiveEquipmentSessions] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [guidancePrescription, setGuidancePrescription] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [eventsResponse, prescriptionsResponse, executionsResponse, equipmentSessionsResponse] = await Promise.all([
        schedule.getAll(),
        connectedTraining.getWorkoutPrescriptions(),
        connectedTraining.getRecentWorkoutExecutions({ limit: 80 }),
        connectedTraining.getCurrentEquipmentWorkoutSessions(),
      ]);
      setEvents(Array.isArray(eventsResponse.data) ? eventsResponse.data : []);
      setWorkoutPrescriptions(Array.isArray(prescriptionsResponse.data) ? prescriptionsResponse.data : []);
      setWorkoutExecutions(Array.isArray(executionsResponse.data) ? executionsResponse.data : []);
      setActiveEquipmentSessions(Array.isArray(equipmentSessionsResponse.data) ? equipmentSessionsResponse.data : []);
    } catch (error) {
      toast.error(t('schedule.loadError'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await schedule.create({
        ...formData,
        athlete_id: user?.id,
        date: new Date(formData.date).toISOString(),
      });
      toast.success(t('schedule.addSuccess'));
      setShowModal(false);
      markScheduleDataUpdated();
      fetchEvents();
      setFormData(emptyForm());
    } catch (error) {
      toast.error(t('schedule.addError'));
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await schedule.delete(eventId);
      toast.success(t('schedule.deleteSuccess'));
      markScheduleDataUpdated();
      fetchEvents();
    } catch (error) {
      toast.error(t('schedule.deleteError'));
    }
  };

  const handleWorkoutExecutionChanged = useCallback((nextExecution) => {
    if (!nextExecution?.id) return;
    setWorkoutExecutions((prev) => {
      const withoutCurrent = prev.filter((execution) => execution?.id !== nextExecution.id);
      return [nextExecution, ...withoutCurrent].sort((a, b) => workoutExecutionDate(b) - workoutExecutionDate(a));
    });
    if (nextExecution.status === 'completed') {
      fetchEvents();
    }
  }, [fetchEvents]);

  const locale = LOCALES[i18n.language] || enGB;
  const weekDaysLabels = t('schedule.weekDays', { returnObjects: true });
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const daysInCalendar = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const selectedWeekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const selectedWeekDays = eachDayOfInterval({
    start: selectedWeekStart,
    end: endOfWeek(selectedDate, { weekStartsOn: 1 }),
  });

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => eventDate(a) - eventDate(b) || String(a.start_time).localeCompare(String(b.start_time))),
    [events],
  );
  const upcomingEvents = useMemo(() => sortedEvents.filter(isUpcoming), [sortedEvents]);
  const todayEvents = useMemo(
    () => sortedEvents.filter((event) => isSameDay(eventDate(event), new Date())),
    [sortedEvents],
  );
  const weekEvents = useMemo(
    () => sortedEvents.filter((event) => selectedWeekDays.some((day) => isSameDay(eventDate(event), day))),
    [sortedEvents, selectedWeekDays],
  );
  const selectedDayEvents = useMemo(
    () => sortedEvents.filter((event) => isSameDay(eventDate(event), selectedDate)),
    [sortedEvents, selectedDate],
  );

  const getEventsForDay = useCallback(
    (day) => sortedEvents.filter((event) => isSameDay(eventDate(event), day)),
    [sortedEvents],
  );

  const openCreateForDay = (day = selectedDate) => {
    setSelectedDate(day);
    setFormData((prev) => ({
      ...prev,
      date: dateKey(day),
    }));
    setShowModal(true);
  };

  if (loading) return <ScheduleSkeleton />;
  if (loadFailed) return <ScheduleErrorState onRetry={fetchEvents} t={t} />;

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="schedule-page">
      <div className="mx-auto max-w-7xl space-y-5">
        <ScheduleHero
          todayEvents={todayEvents}
          nextEvent={upcomingEvents[0]}
          upcomingCount={upcomingEvents.length}
          onAdd={() => openCreateForDay(new Date())}
          t={t}
          locale={locale}
        />

        <div className="grid gap-4 md:grid-cols-3">
          <SummaryCard
            icon={<CalendarDays size={22} strokeWidth={1.8} />}
            label={t('schedule.today')}
            value={todayEvents.length}
            detail={format(new Date(), 'dd MMMM yyyy', { locale })}
          />
          <SummaryCard
            icon={<Timer size={22} strokeWidth={1.8} />}
            label={t('schedule.upcoming')}
            value={upcomingEvents.length}
            detail={t('schedule.upcomingDetail')}
            tone="blue"
          />
          <SummaryCard
            icon={<Trophy size={22} strokeWidth={1.8} />}
            label={t('schedule.week')}
            value={weekEvents.length}
            detail={`${format(selectedWeekDays[0], 'dd MMM', { locale })} - ${format(selectedWeekDays[6], 'dd MMM', { locale })}`}
            tone="lime"
          />
        </div>

        <PrescriptionSchedulePanel
          prescriptions={workoutPrescriptions}
          events={events}
          onOpenGuidance={setGuidancePrescription}
          t={t}
          locale={locale}
        />

        <EquipmentWorkoutTrackingPanel
          activeSessions={activeEquipmentSessions}
          onRefresh={fetchEvents}
          t={t}
        />

        <section className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
                {t('schedule.calendarHeader')}
              </p>
              <h2 className="mt-1 text-2xl font-black tracking-normal text-zinc-950">
                {format(currentDate, 'LLLL yyyy', { locale })}
              </h2>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <ViewToggle viewMode={viewMode} setViewMode={setViewMode} t={t} />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentDate((date) => subMonths(date, 1))}
                  className="inline-flex min-h-11 w-11 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-700 transition hover:bg-zinc-50"
                  aria-label={t('schedule.previousMonth')}
                >
                  <ChevronLeft size={18} strokeWidth={1.8} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    setCurrentDate(now);
                    setSelectedDate(now);
                  }}
                  className="inline-flex min-h-11 items-center justify-center rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
                >
                  {t('schedule.today')}
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentDate((date) => addMonths(date, 1))}
                  className="inline-flex min-h-11 w-11 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-700 transition hover:bg-zinc-50"
                  aria-label={t('schedule.nextMonth')}
                >
                  <ChevronRight size={18} strokeWidth={1.8} />
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'month' ? (
            <CalendarGrid
              days={daysInCalendar}
              currentDate={currentDate}
              getEventsForDay={getEventsForDay}
              openCreateForDay={openCreateForDay}
              weekDays={weekDaysLabels}
              t={t}
            />
          ) : (
            <div className="space-y-5">
              <WeekStrip
                weekDays={selectedWeekDays}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                getEventsForDay={getEventsForDay}
                locale={locale}
              />
              <DayAgendaList
                title={
                  viewMode === 'agenda'
                    ? t('schedule.upcoming')
                    : format(selectedDate, 'dd MMMM yyyy', { locale })
                }
                events={viewMode === 'agenda' ? upcomingEvents : selectedDayEvents}
                onDelete={handleDelete}
                onAdd={() => openCreateForDay(selectedDate)}
                t={t}
                locale={locale}
              />
            </div>
          )}
        </section>

        {viewMode === 'month' ? (
          <DayAgendaList
            title={t('schedule.upcoming')}
            events={upcomingEvents.slice(0, 6)}
            onDelete={handleDelete}
            onAdd={() => openCreateForDay(new Date())}
            t={t}
            locale={locale}
          />
        ) : null}
      </div>

      {showModal && (
        <AddEventModal
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onClose={() => setShowModal(false)}
          t={t}
        />
      )}
      {guidancePrescription ? (
        <WorkoutGuidanceModal
          prescription={guidancePrescription}
          linkedEvent={events.find((event) => event.id === guidancePrescription?.linked_schedule_event_id)}
          initialExecution={latestExecutionForPrescription(workoutExecutions, guidancePrescription?.id)}
          onExecutionChanged={handleWorkoutExecutionChanged}
          onClose={() => setGuidancePrescription(null)}
          t={t}
          locale={locale}
        />
      ) : null}
    </div>
  );
};
